import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Star, Package } from 'lucide-react';
import ImageUploadField from '../../components/ImageUploadField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const { getAuthHeaders } = useAdmin();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    categoria: 'bovinos',
    descricao: '',
    imagem: '',
    destaque: false,
    ordem: 0
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: getAuthHeaders()
      });
      setProducts(response.data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar produtos',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await axios.put(
          `${API}/admin/products/${editingProduct.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast({ title: 'Produto atualizado com sucesso!' });
      } else {
        await axios.post(
          `${API}/admin/products`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast({ title: 'Produto criado com sucesso!' });
      }
      
      setDialogOpen(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao salvar produto',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${id}`, {
        headers: getAuthHeaders()
      });
      toast({ title: 'Produto deletado com sucesso!' });
      loadProducts();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao deletar produto',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      nome: product.nome,
      categoria: product.categoria,
      descricao: product.descricao,
      imagem: product.imagem,
      destaque: product.destaque,
      ordem: product.ordem
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      nome: '',
      categoria: 'bovinos',
      descricao: '',
      imagem: '',
      destaque: false,
      ordem: 0
    });
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Produtos</h1>
          <p className="text-white/60">Gerencie os produtos do cardápio</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black font-bold">
              <Plus className="w-4 h-4 mr-2" />
              Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  className="bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger className="bg-black border-[#cc8b3a]/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/30 text-white">
                    <SelectItem value="bovinos">Bovinos</SelectItem>
                    <SelectItem value="suinos">Suínos</SelectItem>
                    <SelectItem value="aves">Aves</SelectItem>
                    <SelectItem value="defumados">Defumados</SelectItem>
                    <SelectItem value="prontos">Prontos a Servir</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  rows={3}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>

              <div className="space-y-2">
                <ImageUploadField
                  value={formData.imagem}
                  onChange={(url) => setFormData({ ...formData, imagem: url })}
                  getAuthHeaders={getAuthHeaders}
                  label="Imagem do Produto"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="destaque"
                  checked={formData.destaque}
                  onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="destaque" className="cursor-pointer">
                  Produto em Destaque
                </Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-[#cc8b3a]/30 text-white"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black">
                  {editingProduct ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <Card key={product.id} className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
            <CardContent className="p-4">
              <div className="relative aspect-video mb-4 overflow-hidden rounded-lg">
                <img
                  src={product.imagem}
                  alt={product.nome}
                  className="w-full h-full object-cover"
                />
                {product.destaque && (
                  <div className="absolute top-2 right-2 bg-[#cc8b3a] text-black px-2 py-1 rounded text-xs font-bold flex items-center">
                    <Star className="w-3 h-3 mr-1" />
                    DESTAQUE
                  </div>
                )}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{product.nome}</h3>
              <p className="text-sm text-white/70 mb-2 capitalize">
                {product.categoria}
              </p>
              <p className="text-sm text-white/60 mb-4 line-clamp-2">
                {product.descricao}
              </p>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  onClick={() => handleEdit(product)}
                  className="flex-1 bg-[#cc8b3a] hover:bg-[#d4af37] text-black"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(product.id)}
                  className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center py-20">
          <Package className="w-16 h-16 mx-auto text-white/30 mb-4" />
          <p className="text-white/60 text-lg">Nenhum produto cadastrado</p>
          <p className="text-white/40 text-sm">Clique em "Novo Produto" para começar</p>
        </div>
      )}
    </div>
  );
};

export default Products;
